import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import i18n from '@nextroll/ar-i18n';
import { SingleDatePicker } from '@nextroll/ar-date-picker';

import { getValidationError, isValidPropertyValue, ValidationError } from 'util/errors';
import { formatGmtTimezone } from 'util/util';
import { DefinitionInputPropertyFieldBase } from 'types/propertyGeneration';
import { PropertyType, PropertyValidation } from 'types/roflWorkflowActionProperty';
import { useRoflStore } from 'store/roflStore';

import { NodePanelDropDown } from 'components/basic/NodePanelDropDown/NodePanelDropDown';
import { InputErrorElement, InputType } from 'components/basic/inputProperty/InputErrorElement/InputErrorElement';

/**
 *  DateInputDefinitionProps are the type of props needed to update a workflow from this component
 */
export interface DateInputDefinitionUpdateWorkflowParams {
    value: string | { source: 'calculated'; formula: 'current_datetime' | 'current_date' };
}

export interface DateInputDefinitionProps extends
    DefinitionInputPropertyFieldBase<DateInputDefinitionUpdateWorkflowParams> {
}

const TIMEZONE = formatGmtTimezone();

/**
 * Dropdown options of the date type select.
 * Note the labels and descriptions are different between date only vs. date-and-time selects.
 */
const DATE_TYPE_OPTIONS = [
    {
        label: i18n.gettext('Execution Date'),
        info: i18n.gettext('Dynamically send the date in which this action was executed'),
        value: 'execution',
    },
    {
        label: i18n.gettext('Specific Date'),
        info: i18n.gettext('Select a fixed date based on your preferences'),
        value: 'specific',
    },
];
const DATE_AND_TIME_TYPE_OPTIONS = [
    {
        label: i18n.gettext('Execution Date and Time'),
        info: i18n.gettext('Dynamically send the date and time in which this action was executed'),
        value: 'execution',
    },
    {
        label: i18n.gettext('Specific Date and Time'),
        info: i18n.gettext('Select a fixed date and time based on your preferences'),
        value: 'specific',
    },
];

/**
 *  This component represents a DateInputDefinition where a user enters a date,
 *  that date is retained on the "blur" event, and the value is eventually saved on the current workflow
 *  We use the SingleDatePicker component from the ar-date-picker package
 */
export const DateInputDefinition = (props: DateInputDefinitionProps) => {
    const preValue = props.workflowConfig?.[props.inputProperty.name]?.[props.field.name];
    const preDateType = preValue?.formula ? 'execution' : (preValue ? 'specific' : '');

    const [userValue, setUserValue] = useState<string>(preValue);
    const [dateType, setDateType] = useState(preDateType);
    const [error, setError] = useState<React.ReactNode>();

    const shouldShowRequiredError = useRoflStore(state => state.shouldShowRequiredError);
    const fieldDisplayName = props.field?.display_name || '';

    // determine what component to render, i.e. date only vs. date-and-time select
    const isDateTime = props.field?.type === PropertyType.DATETIME;

    // format the timestamp based on it is date only vs. date-and-time.
    const formatDate = useCallback(value => {
        const format = isDateTime ? 'YYYY-MM-DDTHH:mm:ss[Z]' : 'YYYY-MM-DD';
        return i18n.moment.utc(value).format(format);
    }, [isDateTime]);

    const onDateTypeChange = ({ value }: { value: string }) => {
        setDateType(value);
        if (value === 'execution') {
            props.updateWorkflow({ value: { source: 'calculated', formula: isDateTime ? 'current_datetime' : 'current_date' } });
        }
        if (value === 'specific') {
            setUserValue(formatDate(userValue));
            props.updateWorkflow({ value: formatDate(userValue) });
        }
        setError(undefined);
    };
    const onDateValueChange = (date: string) => {
        if (!isValidPropertyValue(date, PropertyValidation.DATE)) {
            setError(getValidationError(ValidationError.INVALID_INPUT, fieldDisplayName));
            return;
        }
        const formattedDate = formatDate(date);
        setUserValue(formattedDate);
        props.updateWorkflow({ value: formattedDate });
        setError(undefined);
    };

    const { identifier, setIsValid, propertyError, hideLabel } = props;

    const setInitialData = useRef(() => {
        if (propertyError?.error) {
            setError(getValidationError(propertyError.error, fieldDisplayName, props.serviceTitle));
            setIsValid((isValid: Record<string, boolean>) => {
                return { ...isValid, [identifier]: false };
            });
        }
    });
    useEffect(() => {
        setInitialData.current();
    }, []);

    useEffect(() => {
        if (shouldShowRequiredError) {
            if (!dateType) {
                setError(i18n.gettext('Please select an option'));
            } else if (dateType !== 'execution' && !userValue) {
                setError(getValidationError(ValidationError.GENERIC_REQUIRED, fieldDisplayName));
            }
        }
        setIsValid((isValid: Record<string, boolean>) => ({
            ...isValid,
            [identifier]: (dateType === 'execution' || !!userValue) && error === undefined,
        }));
    }, [error, identifier, dateType, userValue, shouldShowRequiredError, setIsValid, fieldDisplayName, setError]);

    // specific classname of the data / datetime input
    const dateInputClass = `date-input-definition${isDateTime ? '-datetime' : ''}`;

    // display time; shown in local time
    const displayDateAndTime = useMemo(() => {
        const utcTime = i18n.moment.utc(userValue);
        return (isDateTime ? utcTime.local() : utcTime).format('YYYY-MM-DDTHH:mm:ss');
    }, [userValue, isDateTime]);

    return (
        <div className={dateInputClass} data-testid={dateInputClass}>
            {!hideLabel && (
                <p className='entity-title'>
                    {props.field.display_name}
                </p>
            )}
            <NodePanelDropDown<{ label: string; info: string; value: string }>
                placeholder={i18n.gettext('Select an Option')}
                data={isDateTime ? DATE_AND_TIME_TYPE_OPTIONS : DATE_TYPE_OPTIONS}
                renderOption={option => option.label}
                renderSelected={option => option.value === dateType}
                onSelect={onDateTypeChange}
                className={dateType === 'specific' ? 'mb-2' : ''}
                testId={`workflow-node-panel-dropdown-${isDateTime ? 'datetime' : 'date'}-type`}
            />
            {dateType === 'specific' && (
                <SingleDatePicker
                    onChange={onDateValueChange}
                    i18n={i18n}
                    buttons={[]}
                    showTimeSelector={isDateTime}
                    timeWindow={10}
                    initialValue={displayDateAndTime}
                    allowReclickingSelectedDate
                />
            )}
            {dateType === 'specific' && isDateTime && (
                <span className='date-input-definition-datetime-timezone-description'>
                    <i className='far fa-info-circle' />
                    &nbsp;
                    {i18n.gettext(
                        'The selected date/time will be pushed using your current time zone ({{ timezone }})',
                        { timezone: TIMEZONE },
                    )}
                </span>
            )}
            <InputErrorElement
                value={dateType === 'execution' ? 'execution' : userValue}
                inputType={InputType.TEXT}
                error={error}
                propertyError={propertyError}
                setError={setError}
                setIsValid={setIsValid}
                fieldIsValid={dateType === 'execution' || !!userValue}
                errorClassName='date-input-definition-error-msg'
                identifier={identifier}
                entityTitle={fieldDisplayName}
                serviceTitle={props.serviceTitle}
            />
        </div>
    );
};
